import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiBell } from 'react-icons/fi';
import './NotificationBell.css';

const NotificationBell = ({ items = [], onItemClick, onClearAll }) => {
  const [open, setOpen] = useState(false);
  const [readIds, setReadIds] = useState([]);
  const [dropdownStyle, setDropdownStyle] = useState({ top: 0, right: 0 });
  const wrapperRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);

  const unreadCount = useMemo(
    () => items.filter((item) => !readIds.includes(item.id)).length,
    [items, readIds]
  );

  useEffect(() => {
    if (!open) return;
    const handleClick = (event) => {
      if (
        (wrapperRef.current && wrapperRef.current.contains(event.target)) ||
        (dropdownRef.current && dropdownRef.current.contains(event.target))
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const updatePosition = () => {
      if (!triggerRef.current) return;
      const rect = triggerRef.current.getBoundingClientRect();
      const right = Math.max(16, window.innerWidth - rect.right);
      const top = rect.bottom + 10;
      setDropdownStyle({ top, right });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  const handleItemClick = (item) => {
    setReadIds((prev) => (prev.includes(item.id) ? prev : [...prev, item.id]));
    if (onItemClick) onItemClick(item);
  };

  const handleClearAll = () => {
    setReadIds(items.map((item) => item.id));
    if (onClearAll) onClearAll();
  };

  return (
    <div className="notification-bell" ref={wrapperRef}>
      <button
        type="button"
        className={`notification-trigger ${open ? 'is-open' : ''}`}
        onClick={() => setOpen((prev) => !prev)}
        ref={triggerRef}
      >
        <FiBell size={18} />
        {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
      </button>

      {open &&
        createPortal(
          <div
            className="notification-dropdown"
            ref={dropdownRef}
            style={{ top: dropdownStyle.top, right: dropdownStyle.right }}
          >
            <div className="notification-header">
              <div>
                <h4>Notifications</h4>
                <p>Team updates and reminders.</p>
              </div>
              {items.length > 0 && (
                <button type="button" className="notification-clear" onClick={handleClearAll}>
                  Clear all
                </button>
              )}
            </div>

            <div className="notification-list">
              {items.length === 0 && (
                <div className="notification-empty">No notifications yet.</div>
              )}
              {items.map((item) => {
                const isRead = readIds.includes(item.id);
                return (
                  <button
                    key={item.id}
                    type="button"
                    className={`notification-item ${isRead ? 'is-read' : ''} ${
                      item.tier ? `tier-${item.tier}` : ''
                    }`}
                    onClick={() => handleItemClick(item)}
                  >
                    <div className="notification-item-title">{item.title}</div>
                    {item.description && (
                      <div className="notification-item-desc">{item.description}</div>
                    )}
                    {item.meta && <div className="notification-item-meta">{item.meta}</div>}
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
};

export default NotificationBell;
